import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, LogOut, PackageX, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/store";
import Seo from "../Seo";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const fmtPrice = (p) => (p || p === 0 ? `$${Number(p).toLocaleString()}` : "—");
const removeDate = (iso) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + 14);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export function StaffView({ token, onLogout }) {
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState(null);
  const [busy, setBusy] = useState("");
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/staff/inventory`, { ...auth, params: { search } })
        .then((r) => setProducts(r.data.products))
        .catch((e) => {
          if (e.response?.status === 401 || e.response?.status === 403) onLogout();
          else toast.error("Could not load products");
        });
    }, 350);
    return () => clearTimeout(t);
  }, [search, auth, onLogout]);

  const toggle = async (p) => {
    const next = p.available === false;
    if (!next && !window.confirm(`Mark "${p.title}" as SOLD OUT?\n\nIt shows as Sold Out on the website right away and is removed from the site automatically after 14 days.`)) return;
    setBusy(p.handle);
    try {
      await axios.post(`${API}/staff/stock`, { handle: p.handle, available: next }, auth);
      setProducts((list) => list.map((x) => (x.handle === p.handle ? { ...x, available: next, soldOutAt: next ? null : new Date().toISOString() } : x)));
      toast.success(next ? "Back in stock" : "Marked SOLD OUT");
    } catch {
      toast.error("Could not update — try again");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="max-w-lg mx-auto px-3 pt-6 pb-16" data-testid="staff-view">
      <Seo title="Staff — Stock" noindex />
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="section-eyebrow">Regal Jewels</p>
          <h1 className="font-heading text-2xl text-[#171310]">Stock Control</h1>
        </div>
        <Button variant="outline" onClick={onLogout} className="rounded-none border-[#d8cdb5] h-10" data-testid="staff-logout-btn">
          <LogOut size={14} className="mr-1.5" /> Sign out
        </Button>
      </div>
      <p className="text-xs text-[#8f8571] mb-4">Find a piece and tap SOLD OUT — the website updates instantly. Sold-out pieces are removed from the site automatically after 14 days.</p>

      <div className="sticky top-0 z-10 bg-[#fdfbf5] py-2">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9482]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or SKU..." className="rounded-none border-[#d8cdb5] h-12 pl-9 text-base" data-testid="staff-search" />
        </div>
      </div>

      {!products ? (
        <p className="text-sm text-[#8f8571] text-center py-12">Loading products...</p>
      ) : products.length === 0 ? (
        <p className="text-sm text-[#8f8571] text-center py-12">No products match "{search}"</p>
      ) : (
        <div className="bg-white border border-[#eee6d6] divide-y divide-[#f2ecdd] mt-2" data-testid="staff-list">
          {products.map((p) => {
            const sold = p.available === false;
            return (
              <div key={p.handle} className="flex items-center gap-3 px-3 py-3" data-testid="staff-row">
                <img src={p.images?.[0]} alt="" loading="lazy" className="w-14 h-14 object-contain bg-[#faf7f0] shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#171310] leading-snug line-clamp-2">{p.title}</p>
                  <p className="text-xs font-medium text-[#5c5240] mt-0.5">{fmtPrice(p.price)}</p>
                  {sold && (
                    <p className="text-[10px] text-[#8c2f1b] font-semibold uppercase tracking-wider mt-0.5">
                      Sold out{p.soldOutAt ? ` — off site ${removeDate(p.soldOutAt)}` : ""}
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => toggle(p)}
                  disabled={busy === p.handle}
                  className={`rounded-none h-12 px-3 text-[11px] tracking-[0.08em] uppercase shrink-0 transition-colors duration-300 ${
                    sold
                      ? "bg-white border border-[#d8cdb5] text-[#171310] hover:bg-[#171310] hover:text-[#e9d9ac]"
                      : "bg-[#8c2f1b] hover:bg-[#6e2313] text-white"
                  }`}
                  data-testid={`staff-toggle-${p.handle}`}
                >
                  {sold ? <PackageCheck size={15} className="mr-1.5" /> : <PackageX size={15} className="mr-1.5" />}
                  {busy === p.handle ? "..." : sold ? "In Stock" : "Sold Out"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
